<template>
  <q-card
    class="column cursor-pointer position-relative overflow-hidden"
    :class="{
      [colorClass]: true,
      'justify-center': isMobile && (!item.imageUrl || hideImage)
    }"
    :style="{
      width: itemCount(item) ? `calc(${itemWidth} - 6px)` : itemWidth,
      'min-height': minHeight,
      'max-width': dialog || $q.screen.lt.sm ? undefined : '135px',
      'flex-shrink': $q.screen.gt.sm ? '1' : undefined,
      border: itemCount(item) ? '3px solid var(--q-primary)' : undefined,
      opacity: reduceOpacity({ item }) ? 0.3 : 1
    }"
    flat
    bordered
    data-test-id="item-card"
    @click="handleClick(item)"
  >
    <div>
      <transition
        leave-class="hidden"
        enter-active-class="animated bounceIn"
      >
        <q-badge
          v-if="itemCount(item)"
          :key="itemCount(item)"
          style="font-size: 18px; z-index: 1"
          class="q-py-sm text-bold"
          :color="hideImage ? 'secondary' : 'primary'"
          :text-color="badgeTextColor"
          floating
        >
          x {{ itemCount(item) }}
        </q-badge>
      </transition>
    </div>
    <q-img
      v-if="!!item.imageUrl && !hideImage"
      :src="item.imageUrl"
    />
    <age-restricted-banner
      v-if="item.showAlcoholAcknowledgement"
    />
    <q-card-section
      class="column"
      :class="{
        'q-pa-sm': !!item.imageUrl,
        'justify-between q-pa-md': !item.imageUrl
      }"
      :style="{ height: !item.imageUrl ? '100%' : undefined }"
    >
      <div
        class="text-body2 col column text-weight-bolder"
        :class="{
          'column': isMobile && (!item.imageUrl || hideImage),
          'text-center': textCenter,
          'text-left': !textCenter
        }"
        style="line-height: 1.2;"
        :style="{ 'padding-left': (hideImage || !item.imageUrl) && item.showAlcoholAcknowledgement ? '30px' : undefined }"
      >
        {{ itemName || itemDescription }}
        <slot
          v-if="displayPrice && itemPrice !== null"
          name="price"
        >
          <div class="col text-weight-regular">
            <template v-if="hasExtraCharge">
              {{ $t('pricing.startingAt') }}
            </template>
            <i18n-n
              :value="itemPrice"
              :class="{ 'q-ml-xs': hasExtraCharge }"
              format="currency"
              tag="span"
            />
          </div>
        </slot>
      </div>
    </q-card-section>
    <q-badge
      v-if="showAvailabilityBadge({ item })"
      class="absolute-top-right"
      style="opacity: 1;"
      :color="availabilityBadgeColor({ item })"
      :label="availableStock({ item })"
    />
    <alcohol-acknowledgement-dialog
      ref="alcoholAcknowledgementDialog"
      @alcohol-acknowledgement-concerned="checkItemSalesRestrictions(item)"
    />
  </q-card>
</template>

<script>
import { Cookies, Notify } from 'quasar'
import Item from 'src/models/item/Item'
import Order from 'src/models/order/Order'
import AgeRestrictedBanner from 'src/modules/checkout/components/AgeRestrictedBanner'
import AlcoholAcknowledgementDialog from 'src/modules/checkout/components/AlcoholAcknowledgementDialog'
import SalesRestrictionsMixin from 'src/modules/mixins/SalesRestrictionsMixin'
// This is a hacky fix to prevent the price calculation from causing a recursive loop by using unreactive cache object
const ITEM_ATTRIBUTES_BY_ITEM_IDS = {}

function clearItemAttributesCache () {
  _.each(ITEM_ATTRIBUTES_BY_ITEM_IDS, (_value, key) => {
    delete ITEM_ATTRIBUTES_BY_ITEM_IDS[key]
  })
}

export default {
  name: 'ItemCard',
  components: {
    AlcoholAcknowledgementDialog,
    AgeRestrictedBanner
  },
  mixins: [SalesRestrictionsMixin],
  context: ['currentUser'],
  configuration: ['currentCircuit', 'currentSite'],
  props: {
    item: {
      type: Item,
      required: true
    },
    order: {
      type: Order,
      required: false,
      default: null
    },
    displayPrice: {
      type: Boolean,
      required: false,
      default: true
    },
    itemWidth: {
      type: String,
      required: false,
      default: '18%'
    },
    dialog: {
      type: Boolean,
      required: false,
      default: true
    },
    hideImage: {
      type: Boolean,
      required: false,
      default: false
    },
    bgColor: {
      type: String,
      required: false,
      default: 'responsive'
    },
    textColor: {
      type: String,
      required: false,
      default: 'responsive'
    },
    isMobile: {
      type: Boolean,
      required: false,
      default: false
    },
    hasAlcoholAcknowledgement: {
      type: Boolean,
      required: false,
      default: false
    },
    itemName: {
      type: String,
      required: false,
      default: null
    },
    textCenter: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['click'],
  data () {
    return {
      itemPrice: null,
      itemDescription: null,
      hasExtraCharge: false
    }
  },
  computed: {
    colorClass () {
      return `bg-${this.bgColor} text-${this.textColor}`
    },
    badgeTextColor () {
      if (this.hideImage) return this.$isColorDark(this.currentSite.secondaryColor) ? 'white' : 'black'
      return this.$isColorDark(this.currentSite.primaryColor) ? 'white' : 'black'
    },
    minHeight () {
      if (this.isMobile) {
        return this.itemCount(this.item) ? '74px' : '80px'
      } else {
        return this.itemCount(this.item) ? '144px' : '150px'
      }
    }
  },
  created () {
    clearItemAttributesCache()
  },
  async mounted () {
    setTimeout(async () => {
      await this.setAndCacheAttributesForItem(this.item)
    }, 0)
  },
  methods: {
    handleClick (item) {
      const acknowledgeCookies = Cookies.get(this.$constants.cookies.alcoholAcknowledgement)
      const acknowledgeCookiesSiteId = acknowledgeCookies?.siteId

      const dontAskAgainConfirmed = !!acknowledgeCookies && acknowledgeCookiesSiteId === this.currentSite.id

      if (this.item.showAlcoholAcknowledgement && !dontAskAgainConfirmed && !this.$register) {
        this.$refs.alcoholAcknowledgementDialog.show()
      } else {
        this.checkItemSalesRestrictions()
      }
    },
    checkItemSalesRestrictions () {
      this.checkForSalesRestrictions({
        item: this.item,
        successCallback: (adminApproved = false) => {
          this.$emit('click', adminApproved)
        }
      })
    },
    itemCount (item) {
      return _.filter(this.order.orderItems.data, orderItem => orderItem.itemOrderItem?.itemId === item.id).length
    },
    notifyAdded (item) {
      this.shown = false // Close the dialog after adding the item to the cart
      if (this.$register) return
      Notify.create({
        message: `${item.name} added to Cart`,
        type: 'positive',
        position: 'bottom',
        group: false,
        timeout: 2500,
        classes: 'added-to-cart-notif'
      })
    },
    async setAndCacheAttributesForItem (item) {
      // Calculating prices on each render causes a recursive loop, so we cache the prices
      // after calculating them once.
      if (!_.isNil(ITEM_ATTRIBUTES_BY_ITEM_IDS[item.id])) {
        this.itemPrice = ITEM_ATTRIBUTES_BY_ITEM_IDS[item.id].price
        this.itemDescription = ITEM_ATTRIBUTES_BY_ITEM_IDS[item.id].name
        return
      }

      let orderItem
      ITEM_ATTRIBUTES_BY_ITEM_IDS[item.id] = {}
      if (item.itemPreference) {
        orderItem = this.order.getFavoriteOrderItem(item.itemPreference)
      } else {
        orderItem = this.order.itemOrderItemForItem(item)
      }
      const price = orderItem?.displayPrice || 0
      const itemName = orderItem?.itemOrderItem?.item?.name

      this.hasExtraCharge =
      (item.itemVariations?.data?.length > 1) ||
      (orderItem?.itemOrderItem?.itemModifiers?.data?.length)

      ITEM_ATTRIBUTES_BY_ITEM_IDS[item.id] = {
        price,
        name: itemName
      }

      orderItem?.destroy()
      this.itemPrice = price
      this.itemDescription = itemName
    }
  }
}
</script>
<style lang="scss">
.added-to-cart-notif {
  margin-bottom: 60px;
  margin-top: -50px;
}
</style>
